import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer, ArrowLeft } from "@phosphor-icons/react";

export default function GRNView() {
  const { id } = useParams();
  const [grn, setGrn] = useState(null);
  const [settings, setSettings] = useState({ company_name: "US Operations" });

  useEffect(() => {
    (async () => {
      const [g, s] = await Promise.all([api.get(`/grns/${id}`), api.get("/settings")]);
      setGrn(g.data);
      setSettings(s.data);
    })();
  }, [id]);

  if (!grn) return <div className="font-display uppercase">Loading...</div>;
  const totalQty = grn.lines.reduce((a, b) => a + (b.qty || 0), 0);

  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="grn-view-page">
      <div className="flex items-center justify-between no-print">
        <Link to="/grns" className="brut-tag brut-tag-white inline-flex items-center gap-1">
          <ArrowLeft size={10} weight="bold" /> Back
        </Link>
        <button onClick={() => window.print()} data-testid="print-grn-btn" className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2">
          <Printer size={14} weight="bold" /> Print GRN
        </button>
      </div>

      <div className="brut-card p-8 print-section" data-testid="grn-document">
        <div className="flex justify-between items-start border-b-2 border-[#334155] pb-4">
          <div className="flex items-start gap-3">
            <img src="/logo-gate.jpg" alt="GATE" className="w-16 h-16 brut-border object-cover shrink-0" />
            <div>
              <div className="brut-tag brut-tag-yellow mb-1">Goods Receipt Note</div>
              <div className="font-display text-3xl uppercase tracking-tighter">{settings.company_name}</div>
              <div className="text-xs text-slate-600 mt-1">Route Card · Operations</div>
            </div>
          </div>
          <div className="text-right">
            <div className="brut-label">GRN Number</div>
            <div className="font-display text-2xl">{grn.grn_number}</div>
            <div className="brut-label mt-2">Received Date</div>
            <div className="font-mono-ui">{grn.received_date}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-5">
          <div className="brut-border p-3">
            <div className="brut-label">Supplier</div>
            <div className="font-display text-lg uppercase">{grn.supplier_name}</div>
            <div className="font-mono-ui text-xs">Code: {grn.supplier_code}</div>
          </div>
          <div className="brut-border p-3">
            <div className="brut-label">Vendor Invoice</div>
            <div className="font-display text-lg">{grn.invoice_number}</div>
            <div className="font-mono-ui text-xs">Date: {grn.invoice_date}</div>
          </div>
        </div>

        <div className="mt-5">
          <div className="brut-label mb-2">Line Items</div>
          <table className="brut-table">
            <thead>
              <tr>
                <th>#</th><th>Part #</th><th>Description</th><th>Category</th><th>Qty</th><th>Unit</th><th>Remaining</th><th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {grn.lines.map((l, i) => (
                <tr key={l.line_id}>
                  <td>{i + 1}</td>
                  <td className="font-bold">{l.part_number}</td>
                  <td>{l.part_name}</td>
                  <td><span className="brut-tag brut-tag-yellow">{l.part_category}</span></td>
                  <td className="font-mono-ui">{l.qty}</td>
                  <td>{l.unit}</td>
                  <td className="font-mono-ui">{l.qty_remaining}</td>
                  <td className="text-xs">{l.remarks}</td>
                </tr>
              ))}
              <tr className="font-bold">
                <td colSpan={4} className="text-right">Total Qty</td>
                <td className="font-mono-ui">{totalQty}</td>
                <td colSpan={3}></td>
              </tr>
            </tbody>
          </table>
        </div>

        {grn.remarks && (
          <div className="mt-5 brut-border p-3">
            <div className="brut-label">Remarks</div>
            <div className="text-sm">{grn.remarks}</div>
          </div>
        )}

        <div className="mt-10 grid grid-cols-3 gap-6 text-xs">
          {["Received By", "Verified By", "Authorized By"].map((s) => (
            <div key={s} className="border-t-2 border-[#334155] pt-2 text-center">
              <div className="brut-label">{s}</div>
              <div className="h-12" />
              <div className="text-zinc-500">Signature & Stamp</div>
            </div>
          ))}
        </div>

        <div className="mt-6 text-center text-[10px] tracking-widest uppercase text-zinc-500">
          Created by {grn.created_by_name} · {new Date(grn.created_at).toLocaleString()}
        </div>
      </div>
    </div>
  );
}
